import { RefObject } from 'react'

import { Check } from 'lucide-react'

import { EmptyState, NetworkDisplay } from '~/components/component-library'
import { NetworkFilterState } from '~/components/component-library/TokenSelector/types'
import { RelayNetworkMetadata } from '~/hooks/useRelayNetworks'
import { cn } from '~/utils/tailwind-utils'

type TokenSelectorNetworkListProps = {
  networks: RelayNetworkMetadata[]
  networkFilter: NetworkFilterState
  highlightedIndex: number
  highlightedRef: RefObject<HTMLDivElement | null>
  onSelect: (network: NetworkFilterState) => void
  onHighlight: (index: number) => void
  // Active chains that overflowed the tab row, shown at the top.
  overflowChainIds?: number[]
  onSelectChain?: (chainId: number) => void
}

export function TokenSelectorNetworkList({
  networks,
  networkFilter,
  highlightedIndex,
  highlightedRef,
  onSelect,
  onHighlight,
  overflowChainIds = [],
  onSelectChain,
}: TokenSelectorNetworkListProps) {
  if (networks.length === 0 && overflowChainIds.length === 0) {
    return (
      <div className="flex h-20 w-full items-center p-1">
        <EmptyState className="w-full text-center text-sm">
          No networks found
        </EmptyState>
      </div>
    )
  }

  return (
    <div role="listbox" aria-label="Network list">
      {/* Overflowed active chains, promoted to the top of the
          list and sharing the same keyboard highlight as the relay networks. */}
      {overflowChainIds.map((chainId, index) => {
        const isHighlighted = highlightedIndex === index
        const isSelected =
          networkFilter.type === 'active' && networkFilter.chainId === chainId

        return (
          <div
            key={`overflow-${chainId}`}
            ref={isHighlighted ? highlightedRef : undefined}
            role="option"
            aria-selected={isHighlighted}
            className={cn(
              'flex cursor-pointer items-center justify-between rounded p-2 py-1.5 transition',
              isHighlighted && 'bg-gray-100 dark:bg-gray-800',
              !isHighlighted && 'hover:bg-gray-100 dark:hover:bg-gray-800',
            )}
            onClick={() => onSelectChain?.(chainId)}
            onMouseEnter={() => onHighlight(index)}
          >
            <NetworkDisplay layout="standard" size="small" chainId={chainId} />
            {isSelected && <Check className="size-4" />}
          </div>
        )
      })}
      {networks.map((network, index) => {
        // Relay rows sit after the overflow chains in the highlight order.
        const navIndex = overflowChainIds.length + index
        const isHighlighted = highlightedIndex === navIndex
        const isSelected =
          networkFilter.type === 'relay' && networkFilter.chainId === network.id

        return (
          <div
            key={`relay-${network.id}`}
            ref={isHighlighted ? highlightedRef : undefined}
            role="option"
            aria-selected={isHighlighted}
            className={cn(
              'flex cursor-pointer items-center justify-between rounded p-2 py-1.5 transition',
              isHighlighted && 'bg-gray-100 dark:bg-gray-800',
              !isHighlighted && 'hover:bg-gray-100 dark:hover:bg-gray-800',
            )}
            onClick={() =>
              onSelect({
                type: 'relay',
                chainId: network.id,
                metadata: network,
              })
            }
            onMouseEnter={() => onHighlight(navIndex)}
          >
            <NetworkDisplay
              layout="standard"
              size="small"
              chainId={network.id}
            />
            {isSelected && <Check className="size-4" />}
          </div>
        )
      })}
    </div>
  )
}
